import { useEffect, useState } from 'react';
import styles from './TransactionDetail.module.css';
import PayRentTransactionDetailPanel from './PayRentTransactionDetailPanel';
import { useModal } from '../../hooks/modalContext';
import master from '../../../assets/pages/payment/mastercard.png';
import visa from '../../../assets/pages/payment/visa.png';
import amex from '../../../assets/pages/payment/amex.png';
import { formatDateToDisplayUTCLong } from '../../utils/utils';
import clsx from 'clsx';
import { useTheme } from '../../hooks/ThemeContext';
import { getTenantTransactionById } from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { CancelPendingTransactionAction } from '../../components/CancelPendingTransactionAction';
import { FailureReasonBox } from '../../components/FailureReasonBox';
import { Transactions } from '../../utils/types';

const TransactionDetail = () => {
  const { modalState, setModal } = useModal();
  const { auth } = useAuth();
  const { theme } = useTheme();
  const { transactionDetails, tenantTransactionId, payRentHistoryTransaction } = modalState || {};

  const [transactionData, setTransactionData] = useState<Transactions | typeof transactionDetails | null>(
    transactionDetails || null
  );
  const [isOpen, setOpen] = useState(false);

  const isDark = theme === 'dark';

  useEffect(() => {
    if (payRentHistoryTransaction) return;

    const getTransaction = async () => {
      if (tenantTransactionId) {
        try {
          const response = await getTenantTransactionById(auth?.data?.token, tenantTransactionId);
          if (response) {
            setTransactionData(response);
          }
        } catch (error) {
          console.error('Failed to fetch Transaction Details:', error);
        }
      } else {
        setTransactionData(transactionDetails || null);
      }
    };
    getTransaction();
  }, [tenantTransactionId, payRentHistoryTransaction]);

  if (payRentHistoryTransaction) {
    return (
      <PayRentTransactionDetailPanel
        transaction={payRentHistoryTransaction}
        onClose={() => setModal(null)}
        onCancelled={() => setModal(null, { refreshTransactions: true })}
      />
    );
  }

  const dateTimeString = transactionData?.transactionDate || transactionData?.timestampAdded;
  const dateObj = dateTimeString ? new Date(dateTimeString) : null;

  let date = '';
  let time = '';

  if (dateObj && !isNaN(dateObj.getTime())) {
    const isoString = dateObj.toISOString();
    date = isoString.split('T')[0];
    time = isoString.split('T')[1]?.split('.')[0];
  }

  const getCardImage = (cardCompany: string) => {
    switch (cardCompany?.toLowerCase()) {
      case 'mastercard':
        return master;
      case 'visa':
        return visa;
      case 'amex':
        return amex;
      default:
        return visa;
    }
  };

  const getCardName = (cardCompany: string) => {
    switch (cardCompany?.toLowerCase()) {
      case 'mastercard':
        return 'Mastercard';
      case 'visa':
        return 'Visa card';
      case 'amex':
        return 'American Express card';
      default:
        return 'Visa card';
    }
  };

  if (!transactionData) return <div>Loading...</div>;

  return (
    <main className={styles.main}>
      {/* Header */}
      <div className={`${styles.header} ${isDark && styles.headerDark}`}>
        <h1 className={styles.title}>Transaction details</h1>
        <button className={styles.cross} onClick={() => setModal(null)}>
          ✕
        </button>
      </div>

      {/* Content */}
      <section className={clsx(styles.section, isDark && styles.sectionDark)}>
        {/* Status */}
        <div className={styles.dateOuter}>
          <div className={styles.label}>Payment status:</div>
          <div
            className={clsx(styles.date, {
              [styles.pendingBg]: transactionData?.status?.toLowerCase() === 'pending' || transactionData?.status?.toLowerCase() === 'failed',
              [styles.completedBg]: transactionData?.status === 'Success',
            })}
          >
            {transactionData?.status}
          </div>
        </div>

        {/* Failure Reason - shown when transaction is failed */}
        {transactionData?.status?.toLowerCase() === 'failed' && (
          <FailureReasonBox
            message={
              transactionData?.formattedErrorMessage || transactionData?.errorMessages
            }
            className='mb-2'
          />
        )}

        {/* Date and Time */}
        <div className={styles.dateOuter}>
          <div className={styles.label}>Payment date:</div>
          <div className={clsx(styles.date, isDark && styles.dateDark)}>
            {formatDateToDisplayUTCLong(date)}
          </div>
        </div>
        <div className={styles.dateOuter}>
          <div className={styles.label}>Payment time:</div>
          <div className={clsx(styles.date, isDark && styles.dateDark)}>{`${time} UTC`}</div>
        </div>

        {/* Card Info */}
        <div className={styles.dateOuter}>
          <div className={styles.label}>Payment mode:</div>
          <div className={clsx(styles.date, isDark && styles.dateDark)}>
            {getCardName(transactionData?.cardCompany || '')}
          </div>
        </div>
        <div className={styles.dateOuter}>
          <div className={styles.label}>Card type:</div>
          <div className={styles.imageOuter}>
            <img
              src={getCardImage(transactionData?.cardCompany || '')}
              alt='card'
              className={styles.cardImage}
            />
          </div>
        </div>

        {/* Conditional Fee Details */}
        {isOpen && (
          <>
            <div className={styles.dateOuter}>
              <div className={styles.label}>Rent amount:</div>
              <div className={clsx(styles.date, isDark && styles.dateDark)}>
                ${transactionData?.rentAmount?.toLocaleString()}
              </div>
            </div>
            {transactionData?.fixedFee !== '0.00' && (
              <div className={styles.dateOuter}>
                <div className={styles.label}>Fixed processing fee:</div>
                <div className={clsx(styles.date, isDark && styles.dateDark)}>
                  ${transactionData?.fixedFee?.toLocaleString()}
                </div>
              </div>
            )}
            {transactionData?.percentageProcessingFee !== '0.00' && (
              <div className={styles.dateOuter}>
                <div className={styles.label}>
                  Processing fee ({transactionData?.percentageRate}%):
                </div>
                <div className={clsx(styles.date, isDark && styles.dateDark)}>
                  ${transactionData?.percentageProcessingFee}
                </div>
              </div>
            )}
            <div className={styles.dateOuter}>
              <div className={styles.label}>Total processing fee:</div>
              <div className={clsx(styles.date, isDark && styles.dateDark)}>
                ${transactionData?.processingFee?.toLocaleString()}
              </div>
            </div>
          </>
        )}

        <hr className={styles.line} />

        {/* Amount Paid */}
        <div className={styles.dateOuter}>
          <div className={styles.labelBold}>Total amount paid:</div>
          <div className={styles.imageOuter}>
            <div className={clsx(styles.rent, isDark && styles.dateDark)}>
              ${transactionData?.amountPaid?.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Toggle Button */}
        <button className={styles.summaryBtn} onClick={() => setOpen(!isOpen)}>
          {isOpen ? 'Hide details' : 'View details'}
        </button>

        <CancelPendingTransactionAction
          tenantTransactionId={tenantTransactionId ?? null}
          status={transactionData?.status ?? ''}
          onCancelled={() => setModal(null, { refreshTransactions: true })}
          className='mt-4'
        />
      </section>

      {/* Footer */}
      <div className={clsx(styles.footer, isDark && styles.footerDark)}>
        <div></div>
        <button className={styles.btn} onClick={() => setModal(null)}>
          Close
        </button>
      </div>
    </main>
  );
};

export default TransactionDetail;
